import { notifyOnError } from '~/rage';
import { IObservable, Subject } from '~/rage/utils';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { VarsService } from '../VarsService/VarsService';
import {
	DonateCurrencyShopItemClientConfig,
	DonateCurrencyShopTagClientConfig,
	DonateInventoryShopItemClientConfig,
	IChangeNameApplication,
	IDonateService,
} from './donateServiceTypes';

export class DonateService implements IDonateService {
	private static readonly changeNameApplicationVarKey =
		'donateChangeNameApplication';

	private currencyShopItems = new Subject<DonateCurrencyShopItemClientConfig[]>(
		[],
	);
	private currencyShopTags = new Subject<DonateCurrencyShopTagClientConfig[]>(
		[],
	);
	private inventoryShopItems = new Subject<
		DonateInventoryShopItemClientConfig[]
	>([]);

	constructor(
		private readonly vars: VarsService,
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
	) {
		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config?.donate) {
				return;
			}
			this.currencyShopItems.next(
				(config.donate.currencyShopItems ??
					[]) as DonateCurrencyShopItemClientConfig[],
			);
			this.currencyShopTags.next(
				(config.donate.currencyShopTags ??
					[]) as DonateCurrencyShopTagClientConfig[],
			);
			this.inventoryShopItems.next(
				(config.donate.shopItems ??
					[]) as DonateInventoryShopItemClientConfig[],
			);
		});

		this.backend.addEventHandler(
			'donate:changeNameApplication',
			(application: IChangeNameApplication | null) => {
				this.vars.set(DonateService.changeNameApplicationVarKey, application);
			},
		);
	}

	@notifyOnError
	public async changeName(firstName: string, lastName: string): Promise<void> {
		await this.backend.invoke('donate:changeName', { firstName, lastName });
	}

	public getChangeNameApplication(): IObservable<IChangeNameApplication | null> {
		return this.vars.get<IChangeNameApplication | null>(
			DonateService.changeNameApplicationVarKey,
			null,
		);
	}

	@notifyOnError
	public async cancelChangeName(): Promise<void> {
		await this.backend.invoke('donate:cancelChangeName', {});
	}

	public getCurrencyShopItems(): Subject<DonateCurrencyShopItemClientConfig[]> {
		return this.currencyShopItems;
	}

	public getCurrencyShopTagById(
		id: string,
	): DonateCurrencyShopTagClientConfig | null {
		return this.currencyShopTags.getValue().find((t) => t.id === id) ?? null;
	}

	@notifyOnError
	public async buyCurrencyShopItem(itemId: string): Promise<void> {
		await this.backend.invoke('donate:buyCurrencyShopItem', { itemId });
	}

	public getInventoryShopItems(): Subject<
		DonateInventoryShopItemClientConfig[]
	> {
		return this.inventoryShopItems;
	}

	@notifyOnError
	public async buyDonateShopItem(donateItemId: string): Promise<void> {
		await this.backend.invoke('donate:buyDonateShopItem', {
			donateItemId,
		});
	}
}
